import { Form } from 'components/Wizards/Form/Form'
import Styles from '../../Source.module.scss'
import { Field } from 'components/Wizards/Field/Field'
import { Doc } from 'screens/BrandKits/components/BrandVoice/ContentSourcePicker/FromDoc'
import { useAlgoliaClient } from 'providers/AlgoliaClientProvider'
import { getIndexFilters } from 'screens/blaze/Search/SearchModal/components/useSearch'
import { ALGOLIA_INDEX_SUFFIX } from 'env'
import { useAuth } from 'hooks/UseAuth'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { Configure, InstantSearch } from 'react-instantsearch-dom'
import { SearchResultType } from 'components/Search/constants'
import { SUMMARY_WORDS_LENGTH } from 'screens/blaze/Search/SearchModal/SearchModal'
import { ConnectedDocumentsTable } from './DocumentsTable'
import { ConnectedSearchInput } from './SearchInput'
import { useState, useCallback } from 'react'
import { PdfPreview } from './PdfPreview/PdfPreview'
import { Upload } from 'blaze-ui/icons'
import { Button } from 'blaze-ui/Button'
import { Text } from 'blaze-ui/Text/Text'

const useSearchState = () => {
  const [hasAnyDocuments, setHasAnyDocuments] = useState(false)

  const handleSearchResults = useCallback((results: { nbHits: number }) => {
    // Only update hasAnyDocuments on the initial load (no query)
    // or when explicitly reset
    setHasAnyDocuments(results?.nbHits > 0)
  }, [])

  return {
    hasAnyDocuments,
    handleSearchResults,
  }
}

export const DocumentSource = ({
  setDocument,
  document,
  setPdf,
  pdf,
}: {
  setDocument: (doc: Doc | undefined) => void
  document?: Doc
  setPdf: (pdf: File | undefined) => void
  pdf?: File
}) => {
  const client = useAlgoliaClient()
  const auth = useAuth()
  const currentWorkspaceId = useCurrentWorkspaceId()
  const [searchState, setSearchState] = useState({ query: '' })
  const { hasAnyDocuments, handleSearchResults } = useSearchState()

  const config = {
    indexName: `Article_${ALGOLIA_INDEX_SUFFIX}`,
    indexId: 'articles_index',
    name: SearchResultType.Files,
    attributesToSnippet: [`plain_text:${SUMMARY_WORDS_LENGTH}`],
    snippetEllipsisText: '…',
    visible: true,
    filtersActive: false,
    hitsPerPage: 5,
    searchAttributes: ['category_names', 'title', 'plain_text'],
    filters: getIndexFilters({ workspaceId: currentWorkspaceId, auth, fileType: 'article' }),
  }

  const handleUploadPdf = () => {
    const input = window.document.createElement('input')
    input.type = 'file'
    input.accept = '.pdf'
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (file) {
        setPdf(file)
        setDocument(undefined)
      }
    }
    input.click()
  }

  const handleRemovePdf = () => {
    setPdf(undefined)
  }

  const handleStateChange = (newState: { query: string }) => {
    setSearchState(newState)
  }

  const handleClearSearch = () => {
    setSearchState({ query: '' })
  }

  return (
    <Form.Fields className={Styles.container}>
      {!pdf && (
        <Field label="Select a doc you would like to use as a source">
          <InstantSearch
            searchClient={client}
            indexName={`Article_Sorted_by_Last_Edited_At_${ALGOLIA_INDEX_SUFFIX}`}
            onSearchStateChange={handleStateChange}
            searchState={searchState}
          >
            <Configure
              key={config?.name}
              hitsPerPage={config?.hitsPerPage}
              restrictSearchableAttributes={config?.searchAttributes}
              filters={config?.filters}
              attributesToSnippet={config?.attributesToSnippet}
              snippetEllipsisText={config?.snippetEllipsisText}
            />
            <ConnectedSearchInput placeholder="Search for a doc" onUploadPdf={handleUploadPdf} />
            <ConnectedDocumentsTable
              onClick={setDocument}
              selectedDocumentId={document?.id}
              onUploadPdf={handleUploadPdf}
              clearSearch={handleClearSearch}
              onInitialResults={handleSearchResults}
            />
            {hasAnyDocuments && !pdf && (
              <Field label="Or upload your own doc to use as a source">
                <div className={Styles.uploadPdfContainer}>
                  <Button variant="tertiary" frontIcon={Upload} onPress={handleUploadPdf} size="sm">
                    Choose a PDF
                  </Button>
                  <Text variant="secondary" color="var(--dark-40)">
                    Blaze supports PDF files up to 10MB
                  </Text>
                </div>
              </Field>
            )}
          </InstantSearch>
        </Field>
      )}
      {pdf && (
        <Field label="Or upload your own doc to use as a source">
          <PdfPreview file={pdf} onRemove={handleRemovePdf} />
        </Field>
      )}
    </Form.Fields>
  )
}
